'use client';

import { Camera, LogOut, Mail, Pencil, Save, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { useRef } from 'react';

export interface ProfileInfoRowProps {
	/** Etichetta della riga. */
	label: React.ReactNode;
	/** Valore mostrato a destra. */
	value: React.ReactNode;
	/** Se true, mostra il valore in monospazio colorato ad accento (per dati tipo codice). */
	mono?: boolean;
}

/** Riga etichetta/valore dentro una card di profilo. */
const ProfileInfoRow = ({ label, value, mono }: ProfileInfoRowProps) => (
	<div className="flex items-center justify-between border-b border-border/50 py-2.5">
		<span className="text-[14px] font-semibold text-text-primary">{label}</span>
		<span className={mono ? 'font-mono text-[13px] text-accent' : 'text-[13px] text-text-muted'}>
			{value}
		</span>
	</div>
);
ProfileInfoRow.displayName = 'ProfileInfoRow';

export interface ProfileIdentityCardProps {
	/** Se true, la card è in modalità modifica. */
	editing: boolean;
	/** Se true, il salvataggio del nome è in corso. */
	saving: boolean;
	/** Se true, un upload/rimozione avatar è in corso. */
	uploadingAvatar: boolean;
	/** Nome mostrato in modalità sola lettura. */
	displayName: string;
	/** Valore corrente del campo nome in modifica. */
	draftName: string;
	/** Chiamata a ogni digitazione nel campo nome. */
	onDraftNameChange: (v: string) => void;
	/** Email dell'utente (sola lettura). */
	email: string | null;
	/** URL dell'avatar corrente. */
	avatarUrl: string | null;
	/** Iniziali mostrate quando manca l'avatar. */
	initials: React.ReactNode;
	/** Chiamata al click su "Modifica". */
	onStartEdit: () => void;
	/** Chiamata al click su "Annulla". */
	onCancelEdit: () => void;
	/** Chiamata al click su "Salva". */
	onSave: () => void;
	/** Chiamata alla scelta di un nuovo file avatar. */
	onUploadAvatar: (file: File) => void;
	/** Chiamata alla rimozione dell'avatar corrente. */
	onDeleteAvatar: () => void;
}

/** Card identità del profilo: avatar con upload/rimozione, nome modificabile, email. */
const ProfileIdentityCard = ({
	editing,
	saving,
	uploadingAvatar,
	displayName,
	draftName,
	onDraftNameChange,
	email,
	avatarUrl,
	initials,
	onStartEdit,
	onCancelEdit,
	onSave,
	onUploadAvatar,
	onDeleteAvatar,
}: ProfileIdentityCardProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">Identità</span>
				{!editing ? (
					<button
						type="button"
						onClick={onStartEdit}
						className="inline-flex items-center gap-1.5 rounded-[7px] border border-accent/35 bg-accent/[0.08] px-3 py-1.5 text-[13px] font-semibold text-accent transition-colors hover:bg-accent/[0.14]"
					>
						<Pencil className="h-3 w-3" />
						Modifica
					</button>
				) : (
					<div className="flex gap-2">
						<button
							type="button"
							onClick={onCancelEdit}
							disabled={saving}
							className="inline-flex items-center gap-1.5 rounded-[7px] border border-border px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
						>
							<X className="h-3 w-3" />
							Annulla
						</button>
						<button
							type="button"
							onClick={onSave}
							disabled={saving}
							className="inline-flex items-center gap-1.5 rounded-[7px] bg-accent px-3 py-1.5 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
						>
							<Save className="h-3 w-3" />
							{saving ? 'Salvataggio...' : 'Salva'}
						</button>
					</div>
				)}
			</div>

			<div className="flex items-start gap-5">
				{/* Avatar */}
				<div className="relative shrink-0">
					<button
						type="button"
						className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-accent to-accent-purple text-xl font-extrabold text-black"
						style={{ background: avatarUrl ? 'var(--color-card)' : undefined }}
						onClick={() => {
							if (editing) {
								if (!uploadingAvatar) fileInputRef.current?.click();
							} else if (avatarUrl) {
								window.open(avatarUrl, '_blank');
							}
						}}
						disabled={editing && uploadingAvatar}
						title={editing ? 'Clicca per cambiare immagine' : avatarUrl ? 'Visualizza' : undefined}
					>
						{avatarUrl ? (
							// biome-ignore lint/performance/noImgElement: dynamic avatar from Supabase Storage
							<img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
						) : uploadingAvatar ? (
							<span className="text-sm">…</span>
						) : (
							initials
						)}
						{editing && (
							<div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 opacity-0 transition-opacity hover:opacity-100">
								<Camera className="h-5 w-5 text-white" />
							</div>
						)}
					</button>
					{editing && avatarUrl && (
						<button
							type="button"
							onClick={onDeleteAvatar}
							disabled={uploadingAvatar}
							title="Rimuovi immagine profilo"
							className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-danger text-white shadow-md transition-opacity hover:opacity-80 disabled:opacity-40"
						>
							<Trash2 className="h-3 w-3" />
						</button>
					)}
					<input
						ref={fileInputRef}
						type="file"
						accept="image/jpeg,image/png,image/webp,image/gif"
						className="hidden"
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) onUploadAvatar(file);
							e.target.value = '';
						}}
					/>
				</div>

				{/* Name + email */}
				<div className="flex-1 space-y-3 pt-1">
					<div>
						<label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-text-muted">
							Nome e cognome
						</label>
						{editing ? (
							<input
								type="text"
								value={draftName}
								onChange={(e) => onDraftNameChange(e.target.value)}
								placeholder="Nome e cognome"
								className="w-full rounded-lg border border-accent/40 bg-background px-3 py-2 text-sm font-semibold text-text-primary focus:border-accent focus:outline-none"
							/>
						) : (
							<p className="text-sm font-extrabold text-text-primary">{displayName}</p>
						)}
					</div>
					<div>
						<label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-text-muted">
							Email
						</label>
						<div className="flex items-center gap-1.5">
							<Mail className="h-3.5 w-3.5 text-text-muted" />
							<a
								href={`mailto:${email}`}
								className="text-[14px] text-text-muted transition-colors hover:text-accent"
							>
								{email ?? '-'}
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
ProfileIdentityCard.displayName = 'ProfileIdentityCard';

export interface SignOutCardProps {
	/** Titolo della card. Default: "Esci dall'account". */
	title?: React.ReactNode;
	/** Descrizione sotto il titolo. */
	description?: React.ReactNode;
	/** Testo del bottone. Default: "Esci". */
	label?: React.ReactNode;
	/** Chiamata al click sul bottone di logout. */
	onSignOut: () => void;
}

/** Card con l'azione di logout (colore di pericolo), tipicamente in fondo alla pagina profilo. */
const SignOutCard = ({
	title = "Esci dall'account",
	description = 'Verrai reindirizzato alla pagina di accesso.',
	label = 'Esci',
	onSignOut,
}: SignOutCardProps) => (
	<div className="card">
		<div className="flex items-center justify-between">
			<div>
				<p className="text-[14px] font-semibold text-text-primary">{title}</p>
				<p className="mt-0.5 text-[13px] text-text-muted">{description}</p>
			</div>
			<button
				type="button"
				onClick={onSignOut}
				className="inline-flex items-center gap-1.5 rounded-[7px] border border-danger/30 bg-danger/10 px-4 py-2 text-[13px] font-semibold text-danger transition-colors hover:bg-danger/20"
			>
				<LogOut className="h-3.5 w-3.5" />
				{label}
			</button>
		</div>
	</div>
);
SignOutCard.displayName = 'SignOutCard';

export { ProfileIdentityCard, ProfileInfoRow, SignOutCard };
